// src/pages/departments/HospitalityManagement.js
import { motion } from "framer-motion";

const HospitalityManagement = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden">
        <motion.img
          src="/images/department-hero.jpg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1 }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div
          className="relative text-center text-white px-6"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-3xl md:text-5xl font-extrabold mb-3">
            Department of Hospitality Management
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">
            Excellence in Service, Management, and Innovation
          </p>
        </motion.div>
      </section>

      {/* Content */}
      <div className="px-6 md:px-20 py-16">
        {/* History Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            The <strong>Department of Hospitality Management</strong> at 
            <strong> Federal Polytechnic Bida</strong> was established to meet 
            the growing demand for skilled professionals in the hospitality and 
            tourism sectors. The department plays a vital role in developing 
            service-oriented leaders who can manage hotels, restaurants, and 
            tourism enterprises effectively and ethically.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            The program combines theory and hands-on experience in hotel 
            operations, food production, front office management, catering, 
            and customer service. Students gain both technical and interpersonal 
            skills necessary for global competitiveness in the hospitality industry.
          </p>
          <p className="text-gray-600 leading-relaxed">
            The department is accredited by the <strong>National Board for Technical Education (NBTE)</strong> 
            and offers the <strong>National Diploma (ND)</strong> program, with plans 
            to expand into a <strong>Higher National Diploma (HND)</strong> in the 
            near future. The curriculum is industry-driven, emphasizing excellence, 
            innovation, and professionalism.
          </p>
        </motion.section>

        {/* HOD Section */}
        <motion.section
          className="grid md:grid-cols-3 gap-8 items-center bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="md:col-span-1 flex justify-center">
            <motion.img
              src="/images/hod-profile.jpg"
              alt="HOD"
              className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-blue-200"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            />
          </div>
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Head of Department's Speech
            </h2>
            <motion.p
              className="text-gray-600 leading-relaxed mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Welcome to the <strong>Department of Hospitality Management</strong> 
              at <strong>Federal Polytechnic Bida</strong>. We take pride in 
              training professionals who will redefine hospitality through 
              excellence in service, leadership, and innovation.
            </motion.p>
            <motion.p
              className="text-gray-600 leading-relaxed mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Our curriculum integrates practical learning with academic knowledge, 
              ensuring that our graduates are globally competent, customer-focused, 
              and ethically responsible. We believe that hospitality is not just 
              an industry — it is an attitude of care and professionalism.
            </motion.p>
            <motion.p
              className="text-gray-600 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              I encourage all students to embrace creativity, discipline, and 
              teamwork as they pursue careers in this vibrant field. Together, 
              we will continue to make the department a model of excellence in 
              hospitality education.
            </motion.p>
            <p className="mt-4 text-blue-700 font-semibold">— Mr. John Smith</p>
            <p className="text-gray-500">Head of Department</p>
          </div>
        </motion.section>

        {/* Vision & Mission Section */}
        <motion.section
          className="grid md:grid-cols-2 gap-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Vision</h2>
            <p className="text-gray-600 leading-relaxed">
              To be a <strong>premier institution</strong> for hospitality education, 
              producing competent graduates who deliver exceptional service and 
              leadership in the tourism and hospitality sectors globally.
            </p>
          </div>
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <p className="text-gray-600 leading-relaxed">
              To provide <strong>quality training</strong> and 
              <strong> hands-on experience</strong> in hospitality management, 
              equipping students with technical, managerial, and entrepreneurial 
              skills to excel in hotels, catering, tourism, and related industries.
            </p>
          </div>
        </motion.section>

        {/* Why Study Hospitality Management */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.35 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Why Study Hospitality Management?
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            The hospitality industry is one of the largest and fastest-growing 
            sectors worldwide, offering diverse opportunities for employment, 
            creativity, and entrepreneurship. A career in hospitality allows you 
            to work in exciting environments like hotels, resorts, airlines, 
            cruise lines, and tourism organizations.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            Studying Hospitality Management equips you with leadership, communication, 
            and customer service skills that are valuable in any industry. 
            You’ll learn to manage teams, plan events, and create memorable 
            experiences that enhance business success and customer satisfaction.
          </p>
          <p className="text-gray-600 leading-relaxed">
            At Federal Polytechnic Bida, our program prepares students to be 
            innovators in hospitality — combining technology, sustainability, 
            and service excellence to meet modern industry demands.
          </p>
        </motion.section>

        {/* Career Opportunities Section */}
        <motion.section
          className="bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Career Opportunities and Entry Requirements
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Graduates of Hospitality Management can pursue dynamic careers in 
            both the private and public sectors, including:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4">
            <li>Hotels, Resorts, and Restaurants</li>
            <li>Airlines and Cruise Ship Operations</li>
            <li>Tourism Boards and Travel Agencies</li>
            <li>Event and Conference Management</li>
            <li>Food and Beverage Services</li>
            <li>Hospitality Entrepreneurship</li>
            <li>Teaching and Research Institutions</li>
          </ul>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Entry Requirements</h3>
          <p className="text-gray-600 leading-relaxed">
            Applicants must possess <strong>five O’Level credit passes</strong> in 
            English Language, Mathematics, Biology or Agricultural Science, 
            Economics, and any other relevant subject from WAEC, NECO, or equivalent. 
            Candidates must also meet the <strong>UTME cut-off mark</strong> for admission. 
            The <strong>ND program lasts two years (four semesters)</strong> and emphasizes 
            both academic learning and practical hospitality experience.
          </p>
        </motion.section>

        {/* Gallery Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Department Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["gallery1.jpg", "gallery2.jpg", "gallery3.jpg", "gallery4.jpg"].map(
              (img, i) => (
                <motion.img
                  key={i}
                  src={`/images/${img}`}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-40 md:h-48 object-cover rounded-lg shadow-md"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                />
              )
            )}
          </div>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Department
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> dept.hospitality@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 800 000 0000
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <iframe
            title="Department Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.278884327274!2d6.0125!3d9.0820!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104965e46b2e11f5%3A0xdeadbeef123456!2sFederal%20Polytechnic%20Bida!5e0!3m2!1sen!2sng!4v0000000000000"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.section>
      </div>
    </div>
  );
};

export default HospitalityManagement;
